import { useRichText } from "@hooks/useRichText";
import { type FromSanityGraphQL, type ComponentProps } from "@ts/components";
import { type SectionTextGrid, type TextGridSection } from "@ts/sanity";
import React, { type FC } from "react";

type TextGridInputProps = ComponentProps &
  FromSanityGraphQL<SectionTextGrid> & {
    // Only provided in static. Use "contents" in SSR.
    textGridContents?: SectionTextGrid["contents"];
  };

type TextGridOutputProps = Omit<TextGridInputProps, "contents"> & {
  contents?: TextGridSection[];
};

export const withTextGrid =
  (Component: FC<TextGridOutputProps>) =>
  ({
    name = "TextGrid",
    contents,
    textGridContents,
    analytics,
    ...rest
  }: TextGridInputProps) => {
    /**
     * For static, this comes through aliased as textGridContents because GraphQL throws a strop
     * about there being multiple types for "contents".
     *
     * This is not a problem in GROQ, where the sections simply come through as "contents".
     */
    const _contents = contents ?? textGridContents;

    const { parseContent } = useRichText();

    const mappedContents = _contents?.map((content) => ({
      ...content,
      content: parseContent(content?.content),
    }));

    Component.displayName = name;
    return <Component {...rest} contents={mappedContents} />;
  };
